import { useEffect, useState } from "react";
import { Box, Button, IconButton, Tooltip, Typography } from "@mui/material"
import { GridColDef, GridRowModel } from "@mui/x-data-grid";
import { useSlideInContext } from "../../SlideIn/SlideInProvider";
import { ADD_SUBSCRIPTION } from "../../SlideIn";
import { AddCompanySubscriptionMutation, AddCompanySubscriptionMutationVariables, CompanySubscription, RenewCompanySubscriptionMutation, RenewCompanySubscriptionMutationVariables, Subscription } from "../../../__generated__/graphql";
import { DateCell, HeaderActionType, TableHeader, useManageModal, SelectOption, TableStandard } from "ui";
import { useParams } from "react-router";
import { useMutation, useQuery } from "@apollo/client";
import { GET_COMPANY_SUBSCRIPTIONS, GET_SUBSCRIPTIONS } from "../../../queries";
import { Add, PriorityHigh } from "@mui/icons-material";
import { differenceInDays, format } from "date-fns";
import { RENEW_COMPANY_SUBSCRIPTION, ADD_COMPANY_SUBSCRIPTION } from "../../../mutations";
import { CompanyDataType } from "../../../types";
interface SubscriptionTableProps {
    subscriptions?: CompanySubscription[],
    company: CompanyDataType,
    showHeaderAction?: boolean
}
function mapSubscriptionsToOptions(data: Subscription, index: number): SelectOption {
    return {
        value: data.id,
        label: data.description ?? "N/A",
        isDefault: index === 0,
    }
}
const TABLE_COLUMNS: GridColDef[] = [
    {
        field: "description", headerName: "Type", flex: 2, sortable: true, resizable: false,
        renderCell: ({ value }) => {
            return value ? <div>{value}</div> : ""
        }
    },
    {
        field: "startDate", headerName: "Start Date", flex: 1, sortable: true, resizable: false,
        renderCell: ({ value }) => {
            return value ? <span>{format(value, "MM/dd/yyyy")}</span> : ""
        }
    },
    {
        field: "endDate", headerName: "End Date", flex: 1, sortable: true, resizable: false,
        renderCell: ({ value }) => {
            return value ? <DateCell value={value.toString()} type="subscription" /> : ""
        }
    },
    { field: "status", headerName: "Status", flex: .5, resizable: false }
]
const flattenSubscriptions = (data: any) => {
    const descriptionTrimmed = data.node.subscription.description.split(" ").slice(0, -1).join(" ");
    return {
        startDate: data.node.startDate,
        endDate: data.node.endDate,
        status: data.node.status,
        name: data.node.subscription.name,
        description: descriptionTrimmed,
        timeLeft: differenceInDays(data.node.endDate, Date.now()),
        id: data.node.id,
        ...data.node
    }
}
export function SubscriptionTable({ subscriptions, company, showHeaderAction }: SubscriptionTableProps) {
    const { handleContent, toggleDrawer, open } = useSlideInContext()
    const { id } = useParams()
    const { openModal } = useManageModal();
    const [subOptions, setSubOptions] = useState<SelectOption[]>([]);
    const [renewSubscription] = useMutation<RenewCompanySubscriptionMutation, RenewCompanySubscriptionMutationVariables>(RENEW_COMPANY_SUBSCRIPTION)
    const [addCompanySubscription] = useMutation<AddCompanySubscriptionMutation, AddCompanySubscriptionMutationVariables>(ADD_COMPANY_SUBSCRIPTION)
    const { loading, data, fetchMore } = useQuery(GET_COMPANY_SUBSCRIPTIONS, {
        notifyOnNetworkStatusChange: true,
        variables: {
            companyId: id,
            query: "",
            first: 100
        }
    })
    const { loading: subLoading, data: subData } = useQuery(GET_SUBSCRIPTIONS)
    useEffect(() => {
        if (subData) {
            const temp = subData.subscriptions.filter((e: Subscription) => !company?.companySubscriptions.some(sub => sub.subscription?.name == e.name))
            setSubOptions(temp.map(mapSubscriptionsToOptions))
        }
    }, [subLoading, subData])
    const [pageTableState, setPageTableState] = useState<{
        columns: GridColDef[],
        rows: GridRowModel[]
    }>({
        columns: [],
        rows: []
    })

    useEffect(() => {
        setPageTableState((prevState) => {
            const columns = [...TABLE_COLUMNS]
            const rows = data?.companySubscriptions?.edges?.map(flattenSubscriptions) ?? []
            columns.push({
                field: "renew", headerName: "Renew", sortable: false, flex: .5, resizable: false, editable: false, disableColumnMenu: true,
                renderCell: (params) => {
                    return (
                        <Tooltip title={params.row.timeLeft < 30 ? "Renew Subscription?" : "Too early, cannot renew subscripton"}>
                            <div>
                                <IconButton disabled={params.row.timeLeft >= 30} onClick={() => handleRenewSubscription(params.row.id)}>
                                    {params.row.timeLeft >= 30 ? <Add /> : <PriorityHigh />}
                                </IconButton >
                            </div>
                        </Tooltip>

                    )
                }
            })
            return {
                ...prevState,
                columns,
                rows
            }
        })

    }, [data, loading])
    const addSubscription = () => {
        if (subOptions.length < 1) {
            //this means they have all the subs we currently offer, so we shouldn't show the slide in
            openModal({ type: "NO_SUBSCRIPTIONS_LEFT", cancelButtonLabel: "Close" })
        } else {
            handleContent(ADD_SUBSCRIPTION, {
                subscriptions: subOptions,
                addNewSubscription: async (values: any) => {
                    await handleAddNewSubscription(values)
                    // toggleDrawer()
                },
                loading: subLoading
            })
        }
    }
    const handleAddNewSubscription = async (values: any) => {

        const input = { companyId: company?.id, subscriptionId: values.subscriptionId }
        try {
            const response = await addCompanySubscription({
                variables: { input },
                refetchQueries: [GET_COMPANY_SUBSCRIPTIONS, GET_SUBSCRIPTIONS]
            })
        } catch (err) {
            console.log("err")
        }

    }
    const handleRenewSubscription = async (companySubscriptionId: any) => {
        openModal({ type: "LOADING", cancelButtonLabel: "Close" })
        var response = await renewSubscription({
            variables: { input: { companySubscriptionId: companySubscriptionId } },
            refetchQueries: [GET_COMPANY_SUBSCRIPTIONS]
        })
        if (response.data?.renewCompanySubscription.errors) {
            openModal({ type: "GENERAL_ERROR", cancelButtonLabel: "Close" })
        } else {
            openModal({ type: "SUBSCRIPTION_RENEWED", cancelButtonLabel: "Close" })
        }
    }
    const headerActions: HeaderActionType[] = [
        {
            name: "Add Subscriptions",
            icon: <Add />,
            action: addSubscription
        }
    ]
    return (
        <TableStandard
            loading={loading}
            renderToolbar={({ searchQuery, onSearch, onSearchClear }) => (
                showHeaderAction ? (
                    <TableHeader
                        title="Subscriptions"
                        headerActions={headerActions}
                    />
                ) : (
                    <TableHeader
                        title="Subscriptions"
                    />
                )

            )}
            rows={pageTableState.rows}
            columns={pageTableState.columns}
            pageSize={6}
            // pageInfo={data?.companySubscriptions?.pageInfo}
            // totalRows={data?.companySubscriptions?.totalCount}
            disableBoxShadow
            getRowClassName={(params) => params.row.status === "Active" ? (params.row.timeLeft < 30 ? "Update" : "Active") : "Expired"}
        />
    )
}